// src/pages/dashboard/Premium/components/PremiumToolsGrid.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PremiumQuotas, ToolKey } from '../../api/premiumService';
import { ORDER, icons, FallbackIcon } from './premiumConstants';

interface Props {
  quotas: PremiumQuotas;
  /** Si es true, el usuario no es premium: todas las cards quedan bloqueadas (🔒). */
  locked?: boolean;
}

const PremiumToolsGrid: React.FC<Props> = ({ quotas, locked = false }) => {
  const { t } = useTranslation();

  const quotasByKey: Record<ToolKey, number> = {
    leaked_data: quotas.leaked_data,
    emails: quotas.emails,
    ips: quotas.ips,
    ipwhois: quotas.ipwhois,
    subdomains: quotas.subdomains,
    typosquatting: quotas.typosquatting,
    names_tld: quotas.names_tld,
    whois: quotas.whois,
    dns_all_records: quotas.dns_all_records,
    wafw00f: quotas.wafw00f,
    wappalyzer: quotas.wappalyzer,
    gau: quotas.gau,
    spoofornot: quotas.spoofornot,
    screenshot: quotas.screenshot,
  };

  return (
    <section className="mt-10">
      <div className="mb-6 flex items-center gap-3">
        <span className="h-2 w-2 rounded-full bg-indigo-500" />
        <h2 className="text-xl font-extrabold tracking-tight text-gray-900">
          {t('premium.tools_title')}
        </h2>
      </div>

      <div
        className="grid gap-6 auto-rows-[1fr]"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}
      >
        {ORDER.map((key) => {
          const qty = quotasByKey[key] ?? 0;
          const isLocked = locked;
          const isExhausted = !isLocked && qty === 0;

          const outerRing =
            isLocked
              ? 'bg-gray-200'
              : isExhausted
                ? 'bg-red-200'
                : 'bg-gradient-to-br from-indigo-200 via-fuchsia-200 to-transparent';

          const innerCardBase =
            'relative h-full rounded-2xl p-4 backdrop-blur-sm shadow-sm transition-all duration-200 border';
          const innerCardState = isLocked
            ? 'bg-white/90 border-gray-200 opacity-70 cursor-not-allowed'
            : isExhausted
              ? 'bg-red-50/90 border-red-200'
              : 'bg-white/90 border-gray-200 hover:shadow-md hover:-translate-y-[2px]';

          return (
            <div key={key} className="group relative h-full">
              <div className={`h-full rounded-2xl p-[1px] ${outerRing}`}>
                <div className={`${innerCardBase} ${innerCardState}`}>
                  {/* 🚨 Icono arriba a la derecha */}
                  {isExhausted && (
                    <span
                      className="absolute top-2 right-2 rounded-full bg-red-500 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center shadow"
                      title={t('premium.no_credits') as string}
                    >
                      !
                    </span>
                  )}

                  <div className="flex items-start gap-3">
                    {/* Icono */}
                    <span className="relative inline-flex h-12 w-12 items-center justify-center">
                      {!isLocked && !isExhausted && (
                        <span className="absolute inset-0 rounded-xl bg-gradient-to-tr from-indigo-100 to-fuchsia-100 blur-sm opacity-80" />
                      )}
                      <span className="relative inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white ring-1 ring-gray-100">
                        {icons[key] ?? FallbackIcon}
                      </span>
                    </span>

                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-sm font-semibold text-gray-900">
                        {t(`tools.${key}`)}
                      </h3>

                      <div className="mt-2 flex items-center gap-2">
                        <span
                          className={[
                            'inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold leading-none tabular-nums min-w-[30px]',
                            isLocked
                              ? 'bg-gray-300 text-gray-600'
                              : isExhausted
                                ? 'bg-red-500 text-white'
                                : 'bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white',
                          ].join(' ')}
                        >
                          {qty}
                        </span>
                        <span
                          className={[
                            'text-xs',
                            isLocked || isExhausted ? 'text-gray-500' : 'text-gray-600',
                          ].join(' ')}
                        >
                          {t('premium.exec_count_label')}
                        </span>
                      </div>

                      <p
                        className={[
                          'mt-1 text-xs line-clamp-3',
                          isExhausted ? 'text-red-700/90' : 'text-gray-500',
                        ].join(' ')}
                      >
                        {t(`tools_desc.${key}`)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PremiumToolsGrid;
